import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = ({ user = null, incidentCounts = { critical: 0, high: 0, medium: 0, low: 0 } }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Initialize theme from local storage or default to 'light'
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/main-dashboard',
      icon: 'LayoutDashboard',
      permission: 'view_dashboard'
    },
    {
      label: 'Incidents',
      path: '/incidents-list',
      icon: 'AlertTriangle',
      permission: 'view_incidents',
      badge: incidentCounts?.critical + incidentCounts?.high + incidentCounts?.medium + incidentCounts?.low
    },
    {
      label: 'Reports',
      path: '/reports-analytics',
      icon: 'BarChart3',
      permission: 'view_reports'
    },
    {
      label: 'Blocks',
      path: '/blocks',
      icon: 'Settings',
      permission: 'view_reports'
    },
    {
      label: 'Settings',
      path: '/account-settings',
      icon: 'Settings',
      permission: 'view_settings'
    }
  ];

  const moreMenuItems = [
    {
      label: 'Create Incident',
      path: '/create-incident',
      icon: 'Plus',
      permission: 'create_incident'
    },
    {
      label: 'System Settings',
      path: '/system-settings',
      icon: 'Cog',
      permission: 'admin'
    }
  ];

  const userMenuItems = [
    {
      label: 'Account Settings',
      path: '/account-settings',
      icon: 'User'
    },
    {
      label: 'System Settings',
      path: '/system-settings',
      icon: 'Settings',
      permission: 'admin'
    },
    {
      label: 'Sign Out',
      action: 'logout',
      icon: 'LogOut'
    }
  ];

  const hasPermission = (permission) => {
    if (!permission || !user) return true;
    return user?.permissions?.includes(permission) || user?.role === 'admin';
  };

  const isActiveRoute = (path) => {
    if (path === '/incidents-list') {
      return ['/incidents-list', '/create-incident', '/incident-details']?.includes(location?.pathname);
    }
    return location?.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleUserAction = (action, path) => {
    if (action === 'logout') {
      localStorage.removeItem('xenios_cyberguard_token');
      navigate('/login', {replace: true});
    } else if (path) {
      navigate(path);
    }
    setIsUserMenuOpen(false);
  };

  const getTotalIncidentCount = () => {
    return incidentCounts?.critical + incidentCounts?.high + incidentCounts?.medium + incidentCounts?.low;
  };

  const getBadgeColor = () => {
    if (incidentCounts?.critical > 0) return 'bg-error text-error-foreground';
    if (incidentCounts?.high > 0) return 'bg-warning text-warning-foreground';
    if (incidentCounts?.medium > 0) return 'bg-accent text-accent-foreground';
    return 'bg-success text-success-foreground';
  };

  // Function to toggle the theme
  const toggleTheme = () => {
    setTheme(currentTheme => (currentTheme === 'light' ? 'dark' : 'light'));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(event?.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  // Sync theme with local storage and root element class
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.className = theme;
  }, [theme]);

  return (
    <header className="fixed top-0 left-0 right-0 z-1000 bg-card border-b border-border shadow-light">
      <div className="max-w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => navigate('/main-dashboard')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                <Icon name="Shield" size={20} color="white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-foreground">CyberGuard</h1>
                <p className="text-xs text-muted-foreground -mt-1">Incident Manager</p>
              </div>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems?.filter(item => hasPermission(item?.permission))?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`nav-item relative ${isActiveRoute(item?.path) ? 'active' : ''}`}
              >
                <Icon name={item?.icon} size={18} className="mr-2" />
                {item?.label}
                {item?.badge > 0 && (
                  <span className={`absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium rounded-full min-w-[18px] h-[18px] ${getBadgeColor()}`}>
                    {item?.badge > 99 ? '99+' : item?.badge}
                  </span>
                )}
              </button>
            ))}

            {/* More Menu */}
            {moreMenuItems?.some(item => hasPermission(item?.permission)) && (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="nav-item"
                >
                  <Icon name="MoreHorizontal" size={18} className="mr-2" />
                  More
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-md shadow-medium z-1010">
                    <div className="py-1">
                      {moreMenuItems?.filter(item => hasPermission(item?.permission))?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className="dropdown-item w-full text-left"
                        >
                          <Icon name={item?.icon} size={16} className="mr-2" />
                          {item?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Incident Counter - Desktop Only */}
            <div className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Activity" size={16} />
              <span>Active: {getTotalIncidentCount()}</span>
            </div>

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-muted transition-colors duration-200"
              aria-label="Toggle theme"
            >
              <Icon name={theme === 'light' ? 'Moon' : 'Sun'} size={20} />
            </button>

            {/* User Profile Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-md hover:bg-muted transition-colors duration-200"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                {user && (
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-foreground">{user?.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground">{user?.role || 'Analyst'}</p>
                  </div>
                )}
                <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-md shadow-medium z-1010">
                  <div className="py-1">
                    {userMenuItems?.filter(item => !item?.permission || hasPermission(item?.permission))?.map((item) => (
                      <button
                        key={item?.label}
                        onClick={() => handleUserAction(item?.action, item?.path)}
                        className={`dropdown-item w-full text-left ${item?.action === 'logout' ? 'text-error hover:bg-error hover:text-error-foreground' : ''}`}
                      >
                        <Icon name={item?.icon} size={16} className="mr-2" />
                        {item?.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md hover:bg-muted transition-colors duration-200"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-1020 bg-background/80 backdrop-blur-sm">
          <div ref={mobileMenuRef} className="bg-card border-r border-border w-64 h-full shadow-medium">
            <div className="p-4">
              {/* Mobile Incident Counter */}
              <div className="flex items-center justify-between mb-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="Activity" size={16} />
                  <span className="text-sm font-medium">Active Incidents</span>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBadgeColor()}`}>
                  {getTotalIncidentCount()}
                </span>
              </div>

              {/* Mobile Navigation Items */}
              <nav className="space-y-1">
                {navigationItems?.filter(item => hasPermission(item?.permission))?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center justify-between p-3 rounded-md text-left transition-colors duration-200 ${
                      isActiveRoute(item?.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon name={item?.icon} size={18} className="mr-3" />
                      {item?.label}
                    </div>
                    {item?.badge > 0 && (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBadgeColor()}`}>
                        {item?.badge > 99 ? '99+' : item?.badge}
                      </span>
                    )}
                  </button>
                ))}

                {/* Mobile More Items */}
                {moreMenuItems?.filter(item => hasPermission(item?.permission))?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className="w-full flex items-center p-3 rounded-md text-left hover:bg-muted transition-colors duration-200"
                  >
                    <Icon name={item?.icon} size={18} className="mr-3" />
                    {item?.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;